import { useRef, useEffect, useState } from 'react';
import PalmerSectionLabel from './PalmerSectionLabel';

const categories = ['All', 'AI Strategy', 'Branding', 'Web Design', 'Marketing', 'Growth'];

const services = [
  {
    id: '01',
    category: 'AI Strategy',
    title: 'AI Strategy & Consulting',
    subtitle: 'Intelligence-Driven Direction',
    desc: 'We guide every AI decision from start to finish — ensuring clarity, impact, and measurable results across every touchpoint of your business.',
    tags: ['AI Strategy', 'Consulting', 'Automation'],
    accent: '#fff',
    dark: true,
  },
  {
    id: '02',
    category: 'Branding',
    title: 'Brand Identity Systems',
    subtitle: 'Visual Language & Strategy',
    desc: 'From strategy to execution, we shape consistent brand systems that speak clearly and feel uniquely ownable in any market.',
    tags: ['Identity', 'Logo', 'Guidelines'],
    accent: '#f0f0f0',
    dark: true,
  },
  {
    id: '03',
    category: 'Web Design',
    title: 'Web Design & Development',
    subtitle: 'Performance-Driven Builds',
    desc: 'Design meets execution with real-time, scalable websites — crafted for speed, precision, and performance that converts.',
    tags: ['Design', 'Dev', 'CMS'],
    accent: '#fff',
    dark: true,
  },
  {
    id: '04',
    category: 'Web Design',
    title: 'Motion Direction',
    subtitle: 'Animation & Interaction',
    desc: 'We use motion as a design tool — adding clarity, rhythm, and energy to digital experiences with intention and purpose.',
    tags: ['Motion', 'Animation', 'UX'],
    accent: '#f5f5f5',
    dark: true,
  },
  {
    id: '05',
    category: 'Marketing',
    title: 'Digital Marketing',
    subtitle: 'Reach & Conversion',
    desc: 'Data-driven campaigns that connect your brand to the right audience at the right moment — across every channel.',
    tags: ['SEO', 'Ads', 'Content'],
    accent: '#fff',
    dark: true,
  },
  {
    id: '06',
    category: 'Growth',
    title: 'Growth Systems',
    subtitle: 'Scalable Infrastructure',
    desc: 'We build scalable growth infrastructure — from funnels to automation — that compounds over time and drives sustainable results.',
    tags: ['Funnels', 'Automation', 'Analytics'],
    accent: '#f0f0f0',
    dark: true,
  },
];

function ServiceCard({ service, delay = 0, visible }: { service: typeof services[0]; delay?: number; visible: boolean }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="group border border-white/10 flex flex-col transition-all duration-700 cursor-pointer overflow-hidden"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
        background: hovered ? 'rgba(255,255,255,0.04)' : 'transparent',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Top bar */}
      <div className="border-b border-white/10 px-5 py-3 flex items-center justify-between">
        <span
          className="text-white/25 text-xs"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {service.id}
        </span>
        <span
          className="text-white/25 text-xs"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {service.category}
        </span>
      </div>

      {/* Main content */}
      <div className="px-5 pt-6 pb-5 flex flex-col flex-1">
        {/* Big bold title — Brutal Gallery style */}
        <h3
          className="text-white font-black leading-none mb-1 transition-all duration-300"
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 'clamp(22px, 2.8vw, 38px)',
            letterSpacing: '-0.03em',
            lineHeight: 1,
            transform: hovered ? 'translateX(4px)' : 'translateX(0)',
          }}
        >
          {service.title}
        </h3>
        <p
          className="text-white/30 text-xs mb-4 italic"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {service.subtitle}
        </p>

        {/* Divider */}
        <div className="h-px bg-white/8 mb-4"></div>

        {/* Description */}
        <p
          className="text-white/40 text-xs leading-relaxed flex-1"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {service.desc}
        </p>

        {/* Tags row */}
        <div className="flex flex-wrap gap-1.5 mt-5">
          {service.tags.map((tag) => (
            <span
              key={tag}
              className="border border-white/15 text-white/40 text-xs px-2 py-0.5"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Bottom arrow row */}
      <div
        className="border-t border-white/10 px-5 py-3 flex items-center justify-between transition-all duration-300"
        style={{ background: hovered ? 'rgba(255,255,255,0.06)' : 'transparent' }}
      >
        <span
          className="text-white/20 text-xs"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          View Details
        </span>
        <i
          className="ri-arrow-right-up-line text-white/30 text-sm transition-transform duration-300"
          style={{ transform: hovered ? 'translate(3px, -3px)' : 'translate(0,0)' }}
        ></i>
      </div>
    </div>
  );
}

export default function PalmerServices() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const filtered = activeCategory === 'All'
    ? services
    : services.filter((s) => s.category === activeCategory);

  return (
    <section ref={sectionRef} className="bg-black py-20">
      <PalmerSectionLabel
        left1="© Capabilities サービス内容"
        left2="(WDX® — 04)"
        right="Digital Execution"
      />

      <div className="px-6">
        {/* Big brutal heading */}
        <div
          className="mb-2 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <h2
            className="text-white font-black leading-none"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 'clamp(64px, 11vw, 160px)',
              letterSpacing: '-0.05em',
              lineHeight: 0.88,
            }}
          >
            SERVICES
          </h2>
        </div>

        {/* Subtitle line */}
        <div
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8 transition-all duration-700 delay-100"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <p
            className="text-white/35 text-sm leading-relaxed max-w-md"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            A curated catalog of digital capabilities — built for brands that demand precision, performance, and creative edge.
          </p>
          <span
            className="text-white/20 text-xs"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            ({services.length}) Services Available
          </span>
        </div>

        {/* Category filter pills — Brutal Gallery style */}
        <div
          className="flex flex-wrap gap-2 mb-10 transition-all duration-700 delay-150"
          style={{ opacity: visible ? 1 : 0 }}
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className="px-4 py-1.5 text-xs border transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                borderRadius: '999px',
                background: activeCategory === cat ? 'white' : 'transparent',
                color: activeCategory === cat ? 'black' : 'rgba(255,255,255,0.5)',
                borderColor: activeCategory === cat ? 'white' : 'rgba(255,255,255,0.15)',
                fontWeight: activeCategory === cat ? 600 : 400,
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Service cards grid — Brutal Gallery 3-col grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {filtered.map((service, i) => (
            <div key={service.id} className="bg-black">
              <ServiceCard service={service} delay={i * 60} visible={visible} />
            </div>
          ))}
        </div>

        {/* Bottom line */}
        <div className="mt-0 border-t border-white/10 pt-6 flex items-center justify-between">
          <span className="text-white/20 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Nova AI Digital LLC — All Services
          </span>
          <a
            href="/contact"
            className="text-white text-xs border border-white/20 px-4 py-2 hover:bg-white hover:text-black transition-all duration-300 whitespace-nowrap"
            style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.08em' }}
          >
            GET IN TOUCH →
          </a>
        </div>
      </div>
    </section>
  );
}
